









import { Component, Vue } from 'vue-property-decorator';
import UpsertTunnelGroupsDialog from './UpsertTunnelGroupsDialog.vue';
import notificationService from '@/services/notificationService';
import Datagrid from '@/components/default/Datagrid.vue';
import dxCustomStore from 'devextreme/data/custom_store';
import { DxNavBar } from 'devextreme-vue';
import { CreateTunnelGroupCommand, DataSourceLoadOptions, TunnelGroup, TunnelGroupsClient, ITunnelGroup, UpdateTunnelGroupCommand } from '@/data/web-api-client';
import DataSource from 'devextreme/data/data_source';

@Component({
  components: {
    Datagrid,
    UpsertTunnelGroupsDialog,
    DxNavBar,
  },
})
export default class TunnelGroupsTable extends Vue {
  public loading = false;
  public items: ITunnelGroup[] = [];

  showDialog: boolean = false;

  private tunnelGroupsClient: TunnelGroupsClient = new TunnelGroupsClient(undefined, this.$axios);

  existingTunnelGroup: UpdateTunnelGroupCommand | undefined | null = null;

  existingPriorities: number[] = [];

  private created() {
    this.loadData();
  }

  public async loadData() {
    this.showDialog = false;
    try {
      this.loading = true;
    } catch {
      notificationService.failedToLoad();
    } finally {
      this.loading = false;
    }
  }

  get groupingConfig() {
    return {
      autoExpandAll: false,
      expandMode: 'rowClick',
    };
  }

  public datasource: DataSource = new DataSource({
    store: new dxCustomStore({
      key: 'id',
      load: async (loadOptions) => {
        (loadOptions as any).stringToLower = true;

        const result = await this.tunnelGroupsClient.lookup(new DataSourceLoadOptions(loadOptions));

        return {
          data: result.data,
          totalCount: result.totalCount,
        };
      },
      remove: async (val: any) => {
        try {
          await this.tunnelGroupsClient.deleteSingle(val);
          notificationService.saved();
          this.$emit('refresh');
        } catch (err) {
          notificationService.failedToSave();
        }
      },
      insert: async (val: any) => {
        try {
          await this.tunnelGroupsClient.create(
            new CreateTunnelGroupCommand({
              name: val.name,
              materialMargin: val.materialMargin,
              labourMargin: val.labourMargin,
              priority: val.priority,
            })
          );
          notificationService.saved();
        } catch (err) {
          notificationService.failedToSave();
        }
      },
      update: async (id: any) => {
        try {
          const row = this.gridInstance.getVisibleRows().find((r: any) => r.key == id);

          if (!row) {
            return;
          }

          const TunnelGroups = row.data as ITunnelGroup;

          this.existingTunnelGroup = new UpdateTunnelGroupCommand({
            id: TunnelGroups.id,
            name: TunnelGroups.name,
            materialMargin: TunnelGroups.materialMargin,
            labourMargin: TunnelGroups.labourMargin,
            priority: TunnelGroups.priority,
          });
          notificationService.saved();
        } catch (err) {
          notificationService.failedToSave();
        }
      },
    }),
    paginate: true,
  });

  public async createRow(e: any) {
    const TunnelGroups = e.data as TunnelGroup;

    await this.tunnelGroupsClient.create(
      new CreateTunnelGroupCommand({
        name: TunnelGroups.name,
        materialMargin: TunnelGroups.materialMargin,
        labourMargin: TunnelGroups.labourMargin,
        priority: TunnelGroups.priority,
      })
    );

    this.gridInstance.cancelEditData();
    await this.loadData();
  }

  refresh() {
    this.showDialog = false;
    this.existingTunnelGroup = undefined;
    try {
      this.gridInstance.refresh();
      this.$emit('refresh');
    } catch {
      notificationService.showErrorMessage('Mislukt om de grid te refreshen!');
    }
  }

  public async deleteRow(e: any) {
    e.cancel = new Promise(async (resolve, reject) => {
      try {
        await this.tunnelGroupsClient.deleteSingle(e.data.id);
        this.$emit('refresh');
        notificationService.saved();
        resolve(false);
      } catch (err) {
        notificationService.showAxiosError(err);
        resolve(true);
      }
    });
  }

  private showTunnelGroupsDialog() {
    this.existingPriorities = this.datasource.items()!.map((d) => d.priority!);

    this.showDialog = true;
  }

  get columns() {
    return [
      {
        dataField: 'name',
        caption: 'Naam',
        allowEditing: false,
      },
      {
        dataField: 'labourMargin',
        caption: this.$t('objects.tunnelGroups.labourMargin'),
      },
      {
        dataField: 'materialMargin',
        caption: this.$t('objects.tunnelGroups.materialMargin'),
      },
      {
        dataField: 'priority',
        caption: this.$t('objects.tunnelGroups.priority'),
      },
      {
        type: 'buttons',
        buttons: [
          {
            hint: 'Contract maken',
            icon: 'fas fa-pen',
            visible(e: any) {
              return !e.row.isEditing && !e.row.data.isCompleted;
            },
            onClick: (e: any) => {
              const TunnelGroups = e.row.data as ITunnelGroup;

              this.existingTunnelGroup = new UpdateTunnelGroupCommand({
                id: TunnelGroups.id,
                name: TunnelGroups.name,
                materialMargin: TunnelGroups.materialMargin,
                labourMargin: TunnelGroups.labourMargin,
                priority: TunnelGroups.priority,
              });

              this.showTunnelGroupsDialog();
            },
          },
          'delete',
        ],
      },
    ];
  }

  get toolbarOptions() {
    return [
      {
        location: 'before',
        widget: 'dxButton',
        options: {
          icon: 'fas fa-plus',
          text: 'Toevoegen',
          stylingMode: 'outlined',
          type: 'success',
          onClick: this.showTunnelGroupsDialog,
        },
      },
    ];
  }

  get gridInstance() {
    return ((this.$refs.grid as Vue).$refs.TunnelGroupsGrid as any).instance;
  }
}
